/**
 * Shared predicate for "is this persisted onboardingProgress part of the
 * onboarding journey?".
 *
 * Onboarding is not a single flow — it is a chain of flows that hand off to
 * each other: `onboarding` (welcome) → `character-creation` →
 * `post-character-creation` → `routine-selection` → `onboarding-complete`.
 * The persisted progress records whichever flow the user was in when they
 * stopped, so checking `flowType === "onboarding"` alone misses most of the
 * journey.
 *
 * This used to be duplicated: ExplorePage had a permissive copy listing
 * several flow types, and AppInitializer had a strict one accepting only
 * `"onboarding"` plus the `isOnboarding` collectedData flag. The strict copy
 * treated a user parked mid-onboarding as "stale data" and deleted their
 * progress (UM-287), so both call sites now share this definition.
 */
export interface OnboardingProgressLike {
  flowType?: string;
  collectedData?: Record<string, unknown>;
}

/**
 * Flow types that are part of the onboarding chain.
 *
 * `routine-selection` also runs standalone (the FAB route), but that never
 * reaches this predicate: HybridFlowPage only persists `onboardingProgress`
 * when the ROUTE flow type is "onboarding" (`onProgressUpdate` returns early
 * otherwise), so a persisted "routine-selection" checkpoint can only have
 * come from the onboarding chain.
 */
const ONBOARDING_FLOW_TYPES = new Set([
  "onboarding",
  "character-creation",
  "post-character-creation",
  "routine-selection",
  "onboarding-complete",
]);

export function isOnboardingProgress(
  progress: OnboardingProgressLike | null | undefined
): boolean {
  if (!progress) return false;
  if (progress.collectedData?.isOnboarding === true) return true;
  return !!progress.flowType && ONBOARDING_FLOW_TYPES.has(progress.flowType);
}
